/**
 * Выбор инжектированного кошелька.
 *
 * `window.ethereum` — одна переменная на все расширения: при нескольких
 * установленных кошельках они перезаписывают её друг другом, и панель может
 * говорить не с тем кошельком, который ждёт оператор. EIP-6963 решает это
 * анонсом: каждый кошелёк представляется отдельно, и провайдер выбирается
 * явно, а не гонкой за глобальной переменной.
 */

const PREFERRED_RDNS = 'io.metamask';

const announced = new Map();

if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => {
    const detail = event.detail;
    if (detail?.info?.rdns && detail?.provider) {
      announced.set(detail.info.rdns, detail);
    }
  });

  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

// Запасной путь для кошельков без поддержки EIP-6963.
const pickFromWindow = () => {
  const injected = window.ethereum;
  if (!injected) return null;

  // Несколько расширений сразу — MetaMask складывает соседей в `providers`.
  if (Array.isArray(injected.providers) && injected.providers.length > 0) {
    return (
      injected.providers.find((provider) => provider.isMetaMask) ??
      injected.providers[0]
    );
  }

  return injected;
};

const pick = () => {
  const preferred = announced.get(PREFERRED_RDNS);
  if (preferred) return preferred.provider;

  const first = announced.values().next().value;
  return first ? first.provider : pickFromWindow();
};

/**
 * Расширение внедряется в страницу асинхронно и на холодном старте браузера
 * успевает не всегда до первого рендера. Проверка `window.ethereum` сразу при
 * загрузке даёт ложное «кошелёк не установлен» — поэтому ждём.
 */
export const waitForInjectedProvider = async (timeoutMs = 3000) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    // Кошелёк мог подписаться на запрос позже нас — просим анонс повторно.
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    const provider = pick();
    if (provider) return provider;

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return null;
};

/**
 * Кошелёк, через который оператор подключился: расширение или WalletConnect.
 * Выставляет `WalletProvider`; подпись берёт его, а не найденное расширение —
 * иначе на компьютере с MetaMask подключение по QR подписывало бы расширением.
 */
let active = null;

export const setActiveProvider = (provider) => {
  active = provider;
};

export const getActiveProvider = () => active;

/** Имена анонсированных кошельков — для диагностики в консоли. */
export const listAnnouncedWallets = () =>
  [...announced.values()].map((detail) => detail.info);
